import React, { useState, useRef } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { AdminHeader } from './AdminHeader';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { MasterEventSelector, MasterEvent, SubEvent } from '../pages/MasterEventSelector';
import { MasterEventDashboard } from '../pages/MasterEventDashboard';
import { SubEventDetail } from '../pages/SubEventDetail';
import { ExhibitorsManagement } from '../pages/ExhibitorsManagement';
import { AppUsersManagement } from '../pages/AppUsersManagement';
import { AdminUsersManagement } from '../pages/AdminUsersManagement';
import { AgendaManagement } from '../pages/AgendaManagement';
import { NetworkingManagement } from '../pages/NetworkingManagement';
import { InvitationsManagement } from '../pages/InvitationsManagement';
import { Exhibitor } from '../../../data/exhibitors';
import { AgendaFeature } from '../../../types/agenda';
type AdminView = 'portfolio' | 'portfolio-admin-users' | 'portfolio-settings' | 'master-dashboard' | 'master-exhibitors' | 'master-sponsors' | 'master-agenda' | 'master-networking' | 'master-invitations' | 'master-users' | 'master-settings' | 'sub-event-detail';
export function AdminLayout() {
  const {
    adminLogout
  } = useAdminAuth();
  const [currentView, setCurrentView] = useState<AdminView>('portfolio');
  const [selectedMasterEvent, setSelectedMasterEvent] = useState<MasterEvent | null>(null);
  const [selectedSubEvent, setSelectedSubEvent] = useState<SubEvent | null>(null);
  const [selectedExhibitor, setSelectedExhibitor] = useState<Exhibitor | null>(null);
  const [selectedAgendaFeature, setSelectedAgendaFeature] = useState<AgendaFeature | null>(null);
  const [activePage, setActivePage] = useState('portfolio');
  const [subEventActiveTab, setSubEventActiveTab] = useState<'feed' | 'settings'>('feed');
  // Refs to trigger exit from profile/detail modes
  const exitProfileModeRef = useRef<(() => void) | null>(null);
  const exitAgendaFeatureModeRef = useRef<(() => void) | null>(null);
  const handleLogout = () => {
    adminLogout();
  };
  // Navigation Handlers
  const handleSelectMasterEvent = (event: MasterEvent) => {
    setSelectedMasterEvent(event);
    if (event.subEvents && event.subEvents.length > 0) {
      setCurrentView('master-dashboard');
      setActivePage('dashboard');
    } else {
      setCurrentView('sub-event-detail');
      setActivePage('feed');
      setSubEventActiveTab('feed');
    }
  };
  const handleSelectSubEvent = (subEvent: SubEvent) => {
    setSelectedSubEvent(subEvent);
    setCurrentView('sub-event-detail');
    setActivePage('feed');
    setSubEventActiveTab('feed');
  };
  const handleBackToPortfolio = () => {
    setSelectedMasterEvent(null);
    setSelectedSubEvent(null);
    setSelectedExhibitor(null);
    setSelectedAgendaFeature(null);
    setCurrentView('portfolio');
    setActivePage('portfolio');
  };
  const handleBackToMasterDashboard = () => {
    setSelectedSubEvent(null);
    setSelectedExhibitor(null);
    setSelectedAgendaFeature(null);
    setCurrentView('master-dashboard');
    setActivePage('dashboard');
  };
  const handleBackToExhibitors = () => {
    setSelectedExhibitor(null);
    if (exitProfileModeRef.current) {
      exitProfileModeRef.current();
    }
  };
  const handleBackToAgenda = () => {
    setSelectedAgendaFeature(null);
    if (exitAgendaFeatureModeRef.current) {
      exitAgendaFeatureModeRef.current();
    }
  };
  // Breadcrumbs Logic
  const getBreadcrumbs = () => {
    const crumbs = [{
      label: 'Portfolio',
      onClick: currentView === 'portfolio' ? undefined : handleBackToPortfolio
    }];
    // Portfolio-level pages
    if (currentView === 'portfolio-admin-users') {
      crumbs.push({
        label: 'Admin Users',
        onClick: undefined
      });
      return crumbs;
    } else if (currentView === 'portfolio-settings') {
      crumbs.push({
        label: 'Settings',
        onClick: undefined
      });
      return crumbs;
    }
    // Master Event-level pages
    if (selectedMasterEvent) {
      crumbs.push({
        label: selectedMasterEvent.name,
        onClick: currentView === 'master-dashboard' ? undefined : handleBackToMasterDashboard
      });
      // Add specific page breadcrumb for master event pages
      if (currentView === 'master-exhibitors') {
        if (selectedExhibitor) {
          crumbs.push({
            label: 'Exhibitors',
            onClick: handleBackToExhibitors
          });
          crumbs.push({
            label: 'Manage',
            onClick: undefined
          });
        } else {
          crumbs.push({
            label: 'Exhibitors',
            onClick: undefined
          });
        }
      } else if (currentView === 'master-sponsors') {
        crumbs.push({
          label: 'Sponsors',
          onClick: undefined
        });
      } else if (currentView === 'master-agenda') {
        if (selectedAgendaFeature) {
          crumbs.push({
            label: 'Agenda',
            onClick: handleBackToAgenda
          });
          crumbs.push({
            label: selectedAgendaFeature.name,
            onClick: undefined
          });
        } else {
          crumbs.push({
            label: 'Agenda',
            onClick: undefined
          });
        }
      } else if (currentView === 'master-networking') {
        crumbs.push({
          label: 'Networking',
          onClick: undefined
        });
      } else if (currentView === 'master-invitations') {
        crumbs.push({
          label: 'Invitations',
          onClick: undefined
        });
      } else if (currentView === 'master-users') {
        crumbs.push({
          label: 'App Users',
          onClick: undefined
        });
      } else if (currentView === 'master-settings') {
        crumbs.push({
          label: 'Settings',
          onClick: undefined
        });
      }
    }
    if (selectedSubEvent) {
      crumbs.push({
        label: selectedSubEvent.name,
        onClick: undefined
      });
    }
    return crumbs;
  };
  // Render Content
  const renderContent = () => {
    switch (currentView) {
      case 'portfolio':
        return <MasterEventSelector onSelect={handleSelectMasterEvent} data-id="element-153" />;
      case 'portfolio-admin-users':
        return <AdminUsersManagement onBack={handleBackToPortfolio} data-id="element-154" />;
      case 'portfolio-settings':
        return <div className="text-center py-12" data-id="element-155">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-156">
              Portfolio Settings
            </h2>
            <p className="text-gray-500" data-id="element-157">Coming soon</p>
          </div>;
      case 'master-dashboard':
        if (!selectedMasterEvent) return null;
        return <MasterEventDashboard masterEvent={selectedMasterEvent} onSelectSubEvent={handleSelectSubEvent} data-id="element-158" />;
      case 'master-exhibitors':
        return <ExhibitorsManagement onBack={handleBackToMasterDashboard} onExhibitorSelect={setSelectedExhibitor} onRegisterExitHandler={handler => {
          exitProfileModeRef.current = handler;
        }} data-id="element-159" />;
      case 'master-sponsors':
        return <div className="text-center py-12" data-id="element-160">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-161">
              Sponsors Management
            </h2>
            <p className="text-gray-500" data-id="element-162">Coming soon</p>
          </div>;
      case 'master-agenda':
        return <AgendaManagement onBack={handleBackToMasterDashboard} onFeatureSelect={setSelectedAgendaFeature} onRegisterExitHandler={handler => {
          exitAgendaFeatureModeRef.current = handler;
        }} data-id="element-163" />;
      case 'master-networking':
        return <NetworkingManagement onBack={handleBackToMasterDashboard} data-id="element-164" />;
      case 'master-invitations':
        return <InvitationsManagement onBack={handleBackToMasterDashboard} data-id="element-165" />;
      case 'master-users':
        return <AppUsersManagement onBack={handleBackToMasterDashboard} data-id="element-166" />;
      case 'master-settings':
        return <div className="text-center py-12" data-id="element-167">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-168">
              Event Settings
            </h2>
            <p className="text-gray-500" data-id="element-169">Coming soon</p>
          </div>;
      case 'sub-event-detail':
        if (!selectedMasterEvent) return null;
        return <SubEventDetail subEvent={selectedSubEvent} masterEvent={selectedMasterEvent} onBack={selectedMasterEvent.subEvents && selectedMasterEvent.subEvents.length > 0 ? handleBackToMasterDashboard : handleBackToPortfolio} activeTab={subEventActiveTab} data-id="element-170" />;
      default:
        return <div data-id="element-171">Page not found</div>;
    }
  };
  // Determine Sidebar Level
  const getSidebarLevel = () => {
    if (selectedSubEvent || selectedMasterEvent && currentView === 'sub-event-detail') return 'sub';
    if (selectedMasterEvent) return 'master';
    return 'portfolio';
  };
  return <div className="min-h-screen bg-gray-50 admin-portal" data-id="element-172">
      <AdminSidebar currentLevel={getSidebarLevel()} onNavigate={page => {
      setActivePage(page);
      // Portfolio-level navigation
      if (getSidebarLevel() === 'portfolio') {
        if (page === 'portfolio') {
          setCurrentView('portfolio');
        } else if (page === 'admin-users') {
          setCurrentView('portfolio-admin-users');
        } else if (page === 'settings') {
          setCurrentView('portfolio-settings');
        }
      }
      // Master event level navigation
      else if (getSidebarLevel() === 'master') {
        if (page === 'dashboard') {
          setCurrentView('master-dashboard');
        } else if (page === 'exhibitors') {
          setCurrentView('master-exhibitors');
        } else if (page === 'sponsors') {
          setCurrentView('master-sponsors');
        } else if (page === 'agenda') {
          setCurrentView('master-agenda');
        } else if (page === 'networking') {
          setCurrentView('master-networking');
        } else if (page === 'invitations') {
          setCurrentView('master-invitations');
        } else if (page === 'users') {
          setCurrentView('master-users');
        } else if (page === 'settings') {
          setCurrentView('master-settings');
        }
      }
      // Sub-event level navigation
      else if (getSidebarLevel() === 'sub') {
        if (page === 'feed') {
          setSubEventActiveTab('feed');
        } else if (page === 'settings') {
          setSubEventActiveTab('settings');
        }
      }
    }} onLogout={handleLogout} onBackToPortfolio={handleBackToPortfolio} onBackToMaster={handleBackToMasterDashboard} activePage={activePage} data-id="element-173" />

      <div className="ml-64" data-id="element-174">
        <AdminHeader breadcrumbs={getBreadcrumbs()} exhibitorInfo={selectedExhibitor ? {
        name: selectedExhibitor.name,
        standNumber: selectedExhibitor.standNumber
      } : undefined} data-id="element-175" />
        <main className="p-8" data-id="element-176">{renderContent()}</main>
      </div>
    </div>;
}